import React, { FC, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CreateCampaignRequest,
  CreateCampaignResponse,
  CampaignErrorResponse,
} from '@dreamai/types';
import api from '../../../api';

import { ReactComponent as RocketIcon } from '../../../icons/rocket.svg';
import { ReactComponent as ConversationIcon } from '../../../icons/conversation.svg';
import { ReactComponent as ResearchLinkIcon } from '../../../icons/research-link.svg';
import { ReactComponent as ChevronRightIcon } from '../../../icons/chevron-right.svg';
import './create-campaign-modal.scss';

interface CreateCampaignModalProps {
  show: boolean;
  onClose: () => void;
  agentName?: string;
  /** Optional context ID from a previous conversation where campaign info was captured */
  contextId?: string;
  onOpenResearchModal?: () => void;
}

const CreateCampaignModal: FC<CreateCampaignModalProps> = ({
  show,
  onClose,
  agentName = 'Venn',
  contextId,
  onOpenResearchModal,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subtitle = contextId
    ? `${agentName} has all the information needed. How much help would you like?`
    : `How will ${agentName} create this campaign?`;

  const handleOpenResearch = () => {
    onClose();
    onOpenResearchModal?.();
  };

  const handleCreateCampaign = async (guidedSetup: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const requestBody: CreateCampaignRequest = {
        contextId,
        guidedSetup,
      };

      const response = await api.post<
        CreateCampaignResponse | CampaignErrorResponse
      >('/campaigns', requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const { threadId, agentId } = response.data as CreateCampaignResponse;
        onClose();
        navigate(`/conversations?agent_id=${agentId}&thread_id=${threadId}`);
      } else {
        setError((response.data as CampaignErrorResponse).error);
      }
    } catch (err) {
      console.error('Error creating campaign:', err);
      setError('Failed to create campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size='lg'
      dialogClassName='create-campaign-modal'
    >
      <Modal.Header>
        <Modal.Title>Create new campaign</Modal.Title>
        <button className='cancel-btn' onClick={onClose}>
          Cancel
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className='campaign-wrapper'>
          <p className='create-campaign-subtitle'>{subtitle}</p>

          {error && <p className='create-campaign-error'>{error}</p>}

          <div className='create-campaign-options'>
            <div className='campaign-option quick-setup'>
              <div className='option-icon'>
                <RocketIcon />
              </div>
              <h2 className='option-title'>I know what I'm doing</h2>
              <p className='option-description'>
                Already got your campaign planned out? Choose this option to get
                straight to creating your campaign with {agentName}.
              </p>
              <div className='option-button-wrapper'>
                <button
                  className='option-btn'
                  onClick={() => handleCreateCampaign(false)}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Start quick setup'}
                </button>
              </div>
            </div>

            <div className='campaign-option guided'>
              <div className='option-icon'>
                <ConversationIcon />
              </div>
              <h2 className='option-title'>I need some help</h2>
              <p className='option-description'>
                Prefer to chat things through? Choose this option and{' '}
                {agentName} will give you more guidance and support.
              </p>
              <div className='option-button-wrapper'>
                <button
                  className='option-btn'
                  onClick={() => handleCreateCampaign(true)}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Start guided campaign'}
                </button>
              </div>
            </div>
          </div>

          {onOpenResearchModal && (
            <>
              <div className='campaign-divider'>
                <span>OR</span>
              </div>

              <button className='campaign-link' onClick={handleOpenResearch}>
                <div className='campaign-link-icon'>
                  <ResearchLinkIcon />
                </div>
                <div className='campaign-link-content'>
                  <span className='campaign-link-title'>
                    Need to source companies and research them?
                  </span>
                  <span className='campaign-link-description'>
                    Start company research to delve deep on companies, without
                    messaging.
                  </span>
                </div>
                <ChevronRightIcon className='campaign-link-chevron' />
              </button>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CreateCampaignModal;
